import { Component, input, signal, computed } from '@angular/core';

export interface AccordionItem {
  header: string;
  content: string;
  disabled?: boolean;
  icon?: string;
}

@Component({
  selector: 'app-accordion',
  imports: [],
  template: `
    <div class="space-y-2">
      @for (item of items(); track $index) {
        <div [class]="itemClasses($index)">
          <button
            type="button"
            [disabled]="item.disabled"
            (click)="toggleItem($index)"
            [class]="buttonClasses($index, item.disabled)"
            [attr.aria-expanded]="isOpen($index)"
            [attr.aria-disabled]="item.disabled">
            <div class="flex items-center justify-between w-full">
              <div class="flex items-center gap-3">
                @if (item.icon) {
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                }
                <span class="font-medium text-left">{{ item.header }}</span>
              </div>
              <svg 
                class="w-5 h-5 transition-transform duration-200"
                [class.rotate-180]="isOpen($index)"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          <div 
            [class]="contentClasses($index)"
            [attr.aria-hidden]="!isOpen($index)">
            <div class="pt-4">
              {{ item.content }}
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: []
})
export class AccordionComponent {
  items = input<AccordionItem[]>([]);
  multiple = input<boolean>(false);
  variant = input<'default' | 'bordered' | 'ghost'>('default');

  openItems = signal<Set<number>>(new Set());

  isOpen(index: number): boolean {
    return this.openItems().has(index);
  }

  toggleItem(index: number): void {
    const current = new Set(this.openItems());
    if (current.has(index)) {
      current.delete(index);
    } else {
      if (!this.multiple()) {
        current.clear();
      }
      current.add(index);
    }
    this.openItems.set(current);
  }

  itemClasses(index: number): string {
    const classes = ['border', 'rounded-lg', 'overflow-hidden', 'transition-all', 'duration-200'];
    
    if (this.variant() === 'bordered') {
      classes.push('border-gray-200', 'dark:border-gray-700');
    } else if (this.variant() === 'ghost') {
      classes.push('border-transparent', 'hover:border-gray-200', 'dark:hover:border-gray-700');
    }

    return classes.join(' ');
  }

  buttonClasses(index: number, disabled?: boolean): string {
    const classes = [
      'w-full',
      'px-4',
      'py-3',
      'text-left',
      'flex',
      'items-center',
      'transition-colors',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:ring-offset-2'
    ];

    if (disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-50', 'dark:hover:bg-gray-700');
    }

    if (this.isOpen(index)) {
      classes.push('bg-gray-50', 'dark:bg-gray-700');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800');
    }

    return classes.join(' ');
  }

  contentClasses(index: number): string {
    const classes = [
      'px-4',
      'pb-4',
      'overflow-hidden',
      'transition-all',
      'duration-300',
      'ease-in-out',
      'bg-white',
      'dark:bg-gray-800'
    ];

    if (this.isOpen(index)) {
      classes.push('max-h-[1000px]', 'opacity-100');
    } else {
      classes.push('max-h-0', 'opacity-0');
    }

    return classes.join(' ');
  }
}

